/**
 * VirtualJoystick - Touch joystick for mobile controls
 *
 * Supports two modes:
 * - Fixed mode (default): Always visible at a fixed bottom-left position
 * - Virtual mode: Roblox-style, appears when user starts dragging from the zone
 *
 * Works independently - can be used simultaneously with other touch gestures.
 */
export default class VirtualJoystick {
  // Fixed mode position (padding from screen edge)
  static FIXED_PADDING = 30;

  /**
   * Create a new virtual joystick
   * @param {InputManager} inputManager - The input manager to update keys on
   * @param {Object} options - Configuration options
   */
  constructor(inputManager, options = {}) {
    this.inputManager = inputManager;

    // Mode: 'fixed' (always visible at fixed position) or 'virtual' (Roblox-style)
    this.fixedMode = options.fixedMode !== undefined ? options.fixedMode : true;

    // Configuration - Ellipse zone (centered at bottom-left corner) - used in virtual mode
    this.zoneWidth = options.zoneWidth || 200;      // Ellipse horizontal radius (pixels)
    this.zoneHeight = options.zoneHeight || 370;    // Ellipse vertical radius (pixels)
    this.baseRadius = options.baseRadius || 60;     // Joystick base circle radius
    this.thumbRadius = options.thumbRadius || 30;   // Thumb/knob radius
    this.maxDistance = options.maxDistance || 50;   // Max thumb distance from center
    this.deadzone = options.deadzone || 0.15;       // Deadzone as fraction of maxDistance
    this.activationDelay = options.activationDelay || 120; // ms to wait before activating (virtual mode only)

    // State
    this.active = false;
    this.touchId = null;
    this.pointerId = null; // For pointer event fallback
    this.baseX = 0;
    this.baseY = 0;
    this.thumbX = 0;
    this.thumbY = 0;
    this.inputX = 0;  // -1 to 1
    this.inputY = 0;  // -1 to 1
    this.inputMagnitude = 0;  // 0 to 1, how far joystick is pushed
    this.atEdge = false;  // true when joystick is pushed to max radius

    // Pending activation state (waiting to see if it's a pinch)
    this.pendingTouchId = null;
    this.pendingTouchX = 0;
    this.pendingTouchY = 0;
    this.activationTimer = null;

    // Create DOM elements
    this.createElements();

    // Bind event handlers
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onLostPointerCapture = this.onLostPointerCapture.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);

    // Only enable on touch devices
    if (this.isTouchDevice()) {
      this.enable();
    }
  }

  /**
   * Handle window resize - reposition fixed joystick
   */
  onResize() {
    if (this.fixedMode && !this.active) {
      this.positionFixed();
    }
  }

  /**
   * Handle pointer down - capture pointerId and use setPointerCapture for reliable tracking
   * @param {PointerEvent} e
   */
  onPointerDown(e) {
    // If we just activated via touchstart, capture the corresponding pointerId
    if (this.active && this.pointerId === null && e.pointerType === 'touch') {
      // Check if this pointer is near our joystick base (same touch that activated us)
      const dx = e.clientX - this.baseX;
      const dy = e.clientY - this.baseY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      // Use larger radius for capture to account for thumb movement
      if (distance <= this.maxDistance + 30) {
        this.pointerId = e.pointerId;
        // Use pointer capture on thumb to ensure we receive all events for this pointer
        // even when it moves over other elements like the UI sidebar
        try {
          this.thumb.setPointerCapture(e.pointerId);
        } catch (err) {
          // Pointer capture may fail if pointer is already released
        }
      }
    }
  }

  /**
   * Handle pointer up - fallback for missed touch events
   * Pointer events are more reliably dispatched than touch events
   * @param {PointerEvent} e
   */
  onPointerUp(e) {
    if (!this.active) return;
    if (e.pointerType !== 'touch') return;

    // Check if this matches our tracked pointer
    if (this.pointerId !== null && e.pointerId === this.pointerId) {
      this.deactivate();
      return;
    }

    // Fallback: if we didn't capture pointerId but a touch ended near the joystick, deactivate
    // This catches timing issues where pointerdown fired before touchstart
    if (this.pointerId === null) {
      const dx = e.clientX - this.baseX;
      const dy = e.clientY - this.baseY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= this.maxDistance + 40) {
        this.deactivate();
      }
    }
  }

  /**
   * Handle window blur - deactivate joystick when window loses focus
   * This catches cases where touch ends while app is backgrounded
   */
  onBlur() {
    if (this.active) {
      this.deactivate();
    }
    this.cancelPending();
  }

  /**
   * Handle visibility change - deactivate joystick when page becomes hidden
   * This catches cases where user switches tabs/apps mid-gesture
   */
  onVisibilityChange() {
    if (document.hidden && this.active) {
      this.deactivate();
    }
    if (document.hidden) {
      this.cancelPending();
    }
  }

  /**
   * Handle pointer leaving the document - deactivate if our pointer leaves
   * This catches edge cases where touch moves off screen without proper end event
   * @param {PointerEvent} e
   */
  onPointerLeave(e) {
    if (!this.active) return;
    if (e.pointerType !== 'touch') return;

    // If our tracked pointer left the document, deactivate
    if (this.pointerId !== null && e.pointerId === this.pointerId) {
      this.deactivate();
    }
  }

  /**
   * Handle pointer move - used when pointer is captured to track movement over UI
   * @param {PointerEvent} e
   */
  onPointerMove(e) {
    if (!this.active) return;
    if (this.pointerId === null || e.pointerId !== this.pointerId) return;

    // Calculate distance from base
    const dx = e.clientX - this.baseX;
    const dy = e.clientY - this.baseY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Clamp to max distance
    if (distance > this.maxDistance) {
      const angle = Math.atan2(dy, dx);
      this.thumbX = this.baseX + Math.cos(angle) * this.maxDistance;
      this.thumbY = this.baseY + Math.sin(angle) * this.maxDistance;
    } else {
      this.thumbX = e.clientX;
      this.thumbY = e.clientY;
    }

    // Calculate input values from thumb position
    this.updateInputFromThumb();
    this.updatePosition();
    this.updateKeys();
  }

  /**
   * Handle lost pointer capture - definitive signal that the pointer ended
   * This fires even when touchend is captured by other elements
   * @param {PointerEvent} e
   */
  onLostPointerCapture(e) {
    if (!this.active) return;
    if (this.pointerId !== null && e.pointerId === this.pointerId) {
      this.deactivate();
    }
  }

  /**
   * Set the joystick mode
   * @param {boolean} fixed - True for fixed mode, false for virtual/Roblox mode
   */
  setFixedMode(fixed) {
    if (this.fixedMode === fixed) return;

    this.fixedMode = fixed;

    // Deactivate current joystick state
    this.deactivate();

    // Update visibility based on mode
    if (this.isTouchDevice()) {
      if (fixed) {
        // Fixed mode: show joystick at fixed position (0.7 opacity), hide zone
        this.positionFixed();
        this.container.classList.add('fixed');
        this.container.classList.remove('active'); // 0.7 opacity
        this.zone.style.display = 'none';
        this.debugOverlay.style.display = 'none';
      } else {
        // Virtual mode: hide joystick until touched, show zone
        this.hide();
        this.container.classList.remove('fixed');
        this.zone.style.display = 'block';
      }
    }
  }

  /**
   * Position joystick at fixed bottom-left location
   */
  positionFixed() {
    const padding = VirtualJoystick.FIXED_PADDING;
    this.baseX = padding + this.baseRadius;
    this.baseY = window.innerHeight - padding - this.baseRadius;
    this.thumbX = this.baseX;
    this.thumbY = this.baseY;
    this.updatePosition();
  }

  /**
   * Check if device supports touch
   * @returns {boolean}
   */
  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * Create the joystick DOM elements
   */
  createElements() {
    // Touch trigger zone (invisible, kept for compatibility but not used for hit detection)
    this.zone = document.createElement('div');
    this.zone.className = 'joystick-zone';

    // Debug visualization overlay for ellipse zone
    this.debugOverlay = document.createElement('div');
    this.debugOverlay.id = 'joystick-zone-debug';
    this.updateDebugOverlay();

    // Container for joystick visuals
    this.container = document.createElement('div');
    this.container.className = 'joystick-container';

    // Base circle
    this.base = document.createElement('div');
    this.base.className = 'joystick-base';
    this.base.style.width = `${this.baseRadius * 2}px`;
    this.base.style.height = `${this.baseRadius * 2}px`;

    // Thumb/knob
    this.thumb = document.createElement('div');
    this.thumb.className = 'joystick-thumb';
    this.thumb.style.width = `${this.thumbRadius * 2}px`;
    this.thumb.style.height = `${this.thumbRadius * 2}px`;

    // Assemble
    this.container.appendChild(this.base);
    this.container.appendChild(this.thumb);

    const gameContainer = document.getElementById('container') || document.body;
    gameContainer.appendChild(this.zone);
    gameContainer.appendChild(this.debugOverlay);
    gameContainer.appendChild(this.container);
  }

  /**
   * Update the debug overlay to match current ellipse dimensions
   */
  updateDebugOverlay() {
    const s = this.debugOverlay.style;
    const w = this.zoneWidth;
    const h = this.zoneHeight;

    // Full ellipse positioned so only the top-right quadrant shows in viewport
    s.position = 'fixed';
    s.left = `${-w}px`;
    s.bottom = `${-h}px`;
    s.width = `${w * 2}px`;
    s.height = `${h * 2}px`;
    s.borderRadius = '50%';
    s.background = 'rgba(255, 255, 255, 0.15)';
    s.border = '3px dashed rgba(255, 255, 255, 0.6)';
    s.pointerEvents = 'none';
    s.zIndex = '900'; // Below sidebar (1000) but visible
    s.boxSizing = 'border-box';
    s.display = s.display || 'none';
  }

  /**
   * Set joystick zone dimensions
   * @param {number} width - Ellipse horizontal radius in pixels
   * @param {number} height - Ellipse vertical radius in pixels
   */
  setZoneDimensions(width, height) {
    this.zoneWidth = width;
    this.zoneHeight = height;
    this.updateDebugOverlay();
  }

  /**
   * Show or hide the debug zone overlay
   * @param {boolean} visible
   */
  setDebugVisible(visible) {
    this.debugOverlay.style.display = visible ? 'block' : 'none';
  }

  /**
   * Enable the joystick
   */
  enable() {
    // Listen on document with capture phase to ensure we receive events
    // before other handlers can stop propagation (e.g., sidebar menu)
    document.addEventListener('touchstart', this.onTouchStart, { passive: false, capture: true });
    document.addEventListener('touchmove', this.onTouchMove, { passive: false, capture: true });
    document.addEventListener('touchend', this.onTouchEnd, { passive: false, capture: true });
    document.addEventListener('touchcancel', this.onTouchEnd, { passive: false, capture: true });
    // Pointer events as fallback - more reliable when touch ends over other elements
    window.addEventListener('pointerdown', this.onPointerDown, { capture: true });
    window.addEventListener('pointerup', this.onPointerUp, { capture: true });
    window.addEventListener('pointercancel', this.onPointerUp, { capture: true });
    window.addEventListener('resize', this.onResize);
    // Safety handlers - deactivate on focus loss to prevent stuck joystick
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('pointerleave', this.onPointerLeave);
    // Pointer capture handlers on thumb - these fire when pointer is captured
    this.thumb.addEventListener('pointermove', this.onPointerMove);
    this.thumb.addEventListener('lostpointercapture', this.onLostPointerCapture);

    if (this.fixedMode) {
      // Fixed mode: show joystick at fixed position immediately (0.7 opacity until touched)
      this.positionFixed();
      this.container.classList.add('fixed');
      this.container.classList.remove('active'); // Start at 0.7 opacity
      this.container.style.opacity = ''; // Let CSS control opacity
      this.zone.style.display = 'none';
    } else {
      // Virtual mode: zone visible, joystick hidden until touched
      this.container.classList.remove('fixed');
      this.zone.style.display = 'block';
    }
  }

  /**
   * Disable the joystick
   */
  disable() {
    // Must match the capture option used in enable()
    document.removeEventListener('touchstart', this.onTouchStart, { capture: true });
    document.removeEventListener('touchmove', this.onTouchMove, { capture: true });
    document.removeEventListener('touchend', this.onTouchEnd, { capture: true });
    document.removeEventListener('touchcancel', this.onTouchEnd, { capture: true });
    window.removeEventListener('pointerdown', this.onPointerDown, { capture: true });
    window.removeEventListener('pointerup', this.onPointerUp, { capture: true });
    window.removeEventListener('pointercancel', this.onPointerUp, { capture: true });
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('pointerleave', this.onPointerLeave);
    this.thumb.removeEventListener('pointermove', this.onPointerMove);
    this.thumb.removeEventListener('lostpointercapture', this.onLostPointerCapture);

    this.zone.style.display = 'none';
    this.container.classList.remove('fixed');
    this.hide();
    this.cancelPending();
  }

  /**
   * Check if a touch is on a UI element that should block joystick activation
   * @param {Touch} touch
   * @returns {boolean}
   */
  isOnUI(touch) {
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!el) return false;

    // Check if touch is on toolbar, sheets, settings, game windows, or any interactive UI element
    return el.closest('.top-toolbar, .top-sheet, .settings-panel, .sidebar, .gui-section, .gui-panel, .gw-window, .gw-sidebar, button, input, label, select, textarea, [role="button"]') !== null;
  }

  /**
   * Check if a touch is in the joystick zone (ellipse centered at bottom-left)
   * @param {Touch} touch
   * @returns {boolean}
   */
  isInZone(touch) {
    // Don't activate joystick if touching UI elements
    if (this.isOnUI(touch)) {
      return false;
    }

    // Ellipse centered at bottom-left corner of screen (0, window.innerHeight)
    const centerX = 0;
    const centerY = window.innerHeight;

    // Normalized distance from center (ellipse equation: (x/a)^2 + (y/b)^2 <= 1)
    const dx = touch.clientX - centerX;
    const dy = touch.clientY - centerY;
    const normalizedDist = (dx * dx) / (this.zoneWidth * this.zoneWidth) +
                           (dy * dy) / (this.zoneHeight * this.zoneHeight);

    return normalizedDist <= 1;
  }

  /**
   * Check if a touch is on the fixed joystick base
   * @param {Touch} touch
   * @returns {boolean}
   */
  isOnFixedBase(touch) {
    const dx = touch.clientX - this.baseX;
    const dy = touch.clientY - this.baseY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    // Allow touching within the base radius plus some padding
    return distance <= this.baseRadius + 20;
  }

  /**
   * Handle touch start
   * @param {TouchEvent} e
   */
  onTouchStart(e) {
    // If already active, ignore new touches
    if (this.active) return;

    if (this.fixedMode) {
      // Fixed mode: check if touch is on the joystick base
      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        if (this.isOnFixedBase(touch) && !this.isOnUI(touch)) {
          // Activate immediately - no delay needed in fixed mode
          this.active = true;
          this.touchId = touch.identifier;
          this.container.classList.add('active');

          // Calculate thumb position, clamped to max distance
          const dx = touch.clientX - this.baseX;
          const dy = touch.clientY - this.baseY;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance > this.maxDistance) {
            const angle = Math.atan2(dy, dx);
            this.thumbX = this.baseX + Math.cos(angle) * this.maxDistance;
            this.thumbY = this.baseY + Math.sin(angle) * this.maxDistance;
          } else {
            this.thumbX = touch.clientX;
            this.thumbY = touch.clientY;
          }

          this.updatePosition();
          this.updateInputFromThumb();
          this.updateKeys();
          return;
        }
      }
      return;
    }

    // Virtual mode (Roblox-style) handling below...

    // If we have a pending touch and more touches arrive, cancel pending (it's a pinch)
    if (this.pendingTouchId !== null && e.touches.length > 1) {
      this.cancelPending();
      return;
    }

    // If already have a pending touch, don't start another
    if (this.pendingTouchId !== null) return;

    // Check each new touch to see if it's in our zone
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      if (this.isInZone(touch)) {
        // Check if camera is ACTIVELY being controlled (drag or pinch in progress)
        // If camera is active, activate joystick immediately
        // If camera is only in potential_drag (touch but no movement), use delay for pinch detection
        const cameraActive = this.inputManager?.isCameraActive?.() || false;

        if (cameraActive) {
          // Camera is actively being controlled - activate joystick immediately
          this.active = true;
          this.touchId = touch.identifier;
          this.baseX = touch.clientX;
          this.baseY = touch.clientY;
          this.thumbX = touch.clientX;
          this.thumbY = touch.clientY;
          this.show();
          this.updatePosition();
          return;
        }

        // If there are already multiple touches and we're not pending, this is likely
        // a pinch starting (first touch was outside our zone) - don't activate joystick
        if (e.touches.length > 1) {
          return;
        }

        // Camera not active - use pending activation to detect pinch
        this.pendingTouchId = touch.identifier;
        this.pendingTouchX = touch.clientX;
        this.pendingTouchY = touch.clientY;

        // Set timer to activate after delay
        this.activationTimer = setTimeout(() => {
          this.activateFromPending();
        }, this.activationDelay);

        return;
      }
    }
  }

  /**
   * Cancel pending activation
   */
  cancelPending() {
    if (this.activationTimer) {
      clearTimeout(this.activationTimer);
      this.activationTimer = null;
    }
    this.pendingTouchId = null;
  }

  /**
   * Activate joystick from pending state
   */
  activateFromPending() {
    if (this.pendingTouchId === null) return;

    this.active = true;
    this.touchId = this.pendingTouchId;

    // Position base at original touch location
    this.baseX = this.pendingTouchX;
    this.baseY = this.pendingTouchY;
    this.thumbX = this.pendingTouchX;
    this.thumbY = this.pendingTouchY;

    // Clear pending state
    this.pendingTouchId = null;
    this.activationTimer = null;

    this.show();
    this.updatePosition();

    // Notify InputManager that joystick just activated - it may need to reset camera state
    if (this.inputManager && this.inputManager.onJoystickActivated) {
      this.inputManager.onJoystickActivated(this.touchId);
    }
  }

  /**
   * Deactivate the joystick
   */
  deactivate() {
    // Release pointer capture if we have one (must do before clearing pointerId)
    if (this.pointerId !== null) {
      try {
        this.thumb.releasePointerCapture(this.pointerId);
      } catch (err) {
        // Pointer capture may already be released
      }
    }

    this.active = false;
    this.touchId = null;
    this.pointerId = null;
    this.inputX = 0;
    this.inputY = 0;
    this.inputMagnitude = 0;
    this.atEdge = false;
    this.updateKeys();
    this.cancelPending();

    if (this.fixedMode) {
      // Fixed mode: reset thumb to center, keep visible but remove active state
      this.container.classList.remove('active');
      this.thumbX = this.baseX;
      this.thumbY = this.baseY;
      this.updatePosition();
    } else {
      // Virtual mode: hide joystick
      this.hide();
    }
  }

  /**
   * Calculate input values from thumb position relative to base
   */
  updateInputFromThumb() {
    const dx = this.thumbX - this.baseX;
    const dy = this.thumbY - this.baseY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Track if joystick is at max radius (for running)
    // Use a small tolerance (95% of max) to prevent flickering at the edge
    const edgeThreshold = this.maxDistance * 0.95;
    this.atEdge = distance >= edgeThreshold;

    // Calculate normalized input (-1 to 1)
    const normalizedDistance = Math.min(distance, this.maxDistance) / this.maxDistance;

    if (normalizedDistance < this.deadzone) {
      this.inputX = 0;
      this.inputY = 0;
      this.inputMagnitude = 0;
    } else {
      // Remap from deadzone to 1
      const remapped = (normalizedDistance - this.deadzone) / (1 - this.deadzone);
      const angle = Math.atan2(dy, dx);
      this.inputX = Math.cos(angle) * remapped;
      this.inputY = Math.sin(angle) * remapped;
      this.inputMagnitude = remapped;
    }
  }

  /**
   * Handle touch move
   * @param {TouchEvent} e
   */
  onTouchMove(e) {
    // Update pending touch position if we have one (virtual mode only)
    if (!this.fixedMode && this.pendingTouchId !== null) {
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].identifier === this.pendingTouchId) {
          this.pendingTouchX = e.touches[i].clientX;
          this.pendingTouchY = e.touches[i].clientY;
          break;
        }
      }
    }

    if (!this.active) return;

    // Find our tracked touch
    let touch = null;
    for (let i = 0; i < e.touches.length; i++) {
      if (e.touches[i].identifier === this.touchId) {
        touch = e.touches[i];
        break;
      }
    }

    // Safety check: if our tracked touch is gone (e.g., touchend was captured by another element),
    // deactivate the joystick
    if (!touch) {
      this.deactivate();
      return;
    }

    // Calculate distance from base
    const dx = touch.clientX - this.baseX;
    const dy = touch.clientY - this.baseY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Clamp to max distance
    if (distance > this.maxDistance) {
      const angle = Math.atan2(dy, dx);
      this.thumbX = this.baseX + Math.cos(angle) * this.maxDistance;
      this.thumbY = this.baseY + Math.sin(angle) * this.maxDistance;
    } else {
      this.thumbX = touch.clientX;
      this.thumbY = touch.clientY;
    }

    // Calculate input values from thumb position
    this.updateInputFromThumb();
    this.updatePosition();
    this.updateKeys();
  }

  /**
   * Handle touch end
   * @param {TouchEvent} e
   */
  onTouchEnd(e) {
    // Check if pending touch ended
    if (this.pendingTouchId !== null) {
      let pendingEnded = true;
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].identifier === this.pendingTouchId) {
          pendingEnded = false;
          break;
        }
      }
      if (pendingEnded) {
        this.cancelPending();
      }
    }

    if (!this.active) return;

    // SAFETY: If there are NO touches at all, always deactivate
    // This catches edge cases where touchId tracking fails
    if (e.touches.length === 0) {
      this.deactivate();
      return;
    }

    // Check if our tracked touch ended
    let touchEnded = true;
    for (let i = 0; i < e.touches.length; i++) {
      if (e.touches[i].identifier === this.touchId) {
        touchEnded = false;
        break;
      }
    }

    if (touchEnded) {
      this.deactivate();
    }
  }

  /**
   * Show the joystick
   */
  show() {
    this.container.classList.add('active');
  }

  /**
   * Hide the joystick
   */
  hide() {
    this.container.classList.remove('active');
  }

  /**
   * Update the visual position of base and thumb
   */
  updatePosition() {
    // Position base centered on baseX, baseY
    this.base.style.left = `${this.baseX - this.baseRadius}px`;
    this.base.style.top = `${this.baseY - this.baseRadius}px`;

    // Position thumb centered on thumbX, thumbY
    this.thumb.style.left = `${this.thumbX - this.thumbRadius}px`;
    this.thumb.style.top = `${this.thumbY - this.thumbRadius}px`;
  }

  /**
   * Update the input manager keys based on joystick input
   */
  updateKeys() {
    if (!this.inputManager) return;

    const keys = this.inputManager.keys;
    const threshold = 0.3;  // Threshold for direction activation

    // Horizontal (A/D)
    keys.a = this.inputX < -threshold;
    keys.d = this.inputX > threshold;

    // Vertical (W/S) - Note: screen Y is inverted (up is negative)
    keys.w = this.inputY < -threshold;
    keys.s = this.inputY > threshold;

    // Run when joystick is pushed to the edge (at or past max radius)
    keys.Shift = this.atEdge;
  }

  /**
   * Get the raw joystick input values
   * @returns {{x: number, y: number}} Normalized input from -1 to 1
   */
  getInput() {
    return { x: this.inputX, y: this.inputY };
  }

  /**
   * Check if the joystick is currently active
   * @returns {boolean}
   */
  isActive() {
    return this.active;
  }

  /**
   * Check if there's a pending joystick touch (waiting for activation)
   * @returns {boolean}
   */
  isPending() {
    return this.pendingTouchId !== null;
  }

  /**
   * Get the touch identifier being used by the joystick
   * @returns {number|null} Touch identifier or null if not active
   */
  getTouchId() {
    return this.touchId;
  }

  /**
   * Get the pending touch identifier (if any)
   * @returns {number|null} Pending touch identifier or null
   */
  getPendingTouchId() {
    return this.pendingTouchId;
  }

  /**
   * Check if in fixed mode
   * @returns {boolean} True if in fixed mode
   */
  isFixedMode() {
    return this.fixedMode;
  }

  /**
   * Destroy the joystick and clean up
   */
  destroy() {
    this.disable();
    this.zone.remove();
    this.debugOverlay.remove();
    this.container.remove();
  }
}
